import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Paper, Typography, alpha } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import type { AppDispatch, RootState } from "../store";
import { searchOrders } from "../store/features/orderSlice";
import { searchStandbyOrders } from "../store/features/standbyOrderSlice";
import { fetchOperationsTeams } from "../store/features/operationsTeamSlice";
import DetailHeaderSplitActions from "../components/common/DetailHeaderSplitActions";
import DetailPageHeader from "../components/common/DetailPageHeader";
import OrderTable from "../components/orders/OrderTable";
import { TestId } from "../constants/testIds";
import { canMutateOrders, isStaffOperations, isUserAdmin } from "../types/user";
import { canSelectOrderForReport, type Order, type OrderSearchFilters } from "../types/order";
import { canSelectStandbyForReport, type StandbyOrder } from "../types/standbyOrder";
import type { PaginationParam } from "../types/paginationResponse";
import { createUangJalanReportApi } from "../api/uangJalanReportsApi";
import { getErrorMessage } from "../utils/errorHandling";
import { formatRupiah } from "../utils/formatRupiah";
import { toastEmitter } from "../utils/toastEmitter";
import { getActionRequiredOrderStatusesForUser } from "../utils/actionRequiredStatuses";
import { getDefaultOrderTeamFilterId } from "../utils/defaultOrderTeamFilter";

type TranslationKeys = keyof typeof translations.en;

const DEFAULT_PAGE_SIZE = 10;

const headerButtonSx = {
  textTransform: "none" as const,
  fontWeight: 600,
  borderRadius: 1.5,
};

interface SelectedMemberMeta {
  kind: "trip" | "standby";
  teamOperationsId: string;
}

const OrdersPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const {
    orders,
    loading: tripLoading,
    error: tripError,
    pagination: tripPagination,
  } = useSelector((state: RootState) => state.orders);
  const {
    standbyOrders,
    loading: standbyLoading,
    error: standbyError,
    pagination: standbyPagination,
  } = useSelector((state: RootState) => state.standbyOrders);
  const { operationsTeams } = useSelector((state: RootState) => state.operationsTeams);

  const isActionRequired = location.pathname === "/orders/action-required";
  const actionRequiredStatuses = useMemo(() => getActionRequiredOrderStatusesForUser(user), [user]);

  const initialUrlTeamId = searchParams.get("teamOperationsId") || "";
  const [filters, setFilters] = useState<OrderSearchFilters>({
    status: searchParams.get("status") || "",
    teamOperationsId: initialUrlTeamId,
  });
  const [teamFilterReady, setTeamFilterReady] = useState(Boolean(initialUrlTeamId));
  const [operationsTeamsLoaded, setOperationsTeamsLoaded] = useState(false);
  const [paginationState, setPaginationState] = useState<PaginationParam>({
    page: Number(searchParams.get("page")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || DEFAULT_PAGE_SIZE,
  });
  const [reportSelectMode, setReportSelectMode] = useState(false);
  const [selectedMembersById, setSelectedMembersById] = useState<
    Record<string, SelectedMemberMeta>
  >({});
  const [submittingReport, setSubmittingReport] = useState(false);

  const canAddOrder = isUserAdmin(user) || isStaffOperations(user);
  const canCreateReport = canMutateOrders(user);
  const selectedIds = Object.keys(selectedMembersById);
  const selectedTeamOperationsId =
    selectedIds.length > 0 ? selectedMembersById[selectedIds[0]].teamOperationsId : undefined;
  const loading = tripLoading || standbyLoading;
  const error = tripError || standbyError;
  const combinedTotal = (tripPagination?.total ?? 0) + (standbyPagination?.total ?? 0);
  const pagination = {
    page: paginationState.page,
    pageSize: paginationState.pageSize,
    total: combinedTotal,
    totalPages:
      paginationState.pageSize > 0 ? Math.ceil(combinedTotal / paginationState.pageSize) : 0,
    sortBy: tripPagination?.sortBy || standbyPagination?.sortBy || "",
    sortOrder: tripPagination?.sortOrder || standbyPagination?.sortOrder || "desc",
  };

  const selectedTeam = useMemo(() => {
    if (!filters.teamOperationsId) {
      return null;
    }
    return operationsTeams.find((team) => team._id === filters.teamOperationsId) || null;
  }, [filters.teamOperationsId, operationsTeams]);

  const effectiveFilters = useMemo((): OrderSearchFilters => {
    if (!isActionRequired || actionRequiredStatuses.length === 0) {
      return filters;
    }
    return {
      teamOperationsId: filters.teamOperationsId,
      statuses: actionRequiredStatuses,
    };
  }, [filters, isActionRequired, actionRequiredStatuses]);

  useEffect(() => {
    let cancelled = false;
    dispatch(fetchOperationsTeams()).finally(() => {
      if (!cancelled) {
        setOperationsTeamsLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (teamFilterReady || !user || !operationsTeamsLoaded) {
      return;
    }

    const defaultTeamId = getDefaultOrderTeamFilterId(
      user._id,
      operationsTeams,
      searchParams.get("teamOperationsId"),
    );

    if (defaultTeamId && defaultTeamId !== filters.teamOperationsId) {
      setFilters((current) => ({ ...current, teamOperationsId: defaultTeamId }));
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("teamOperationsId", defaultTeamId);
          next.delete("kind");
          return next;
        },
        { replace: true },
      );
    }

    setTeamFilterReady(true);
  }, [
    filters.teamOperationsId,
    operationsTeams,
    operationsTeamsLoaded,
    searchParams,
    setSearchParams,
    teamFilterReady,
    user,
  ]);

  const actionRequiredEmpty = isActionRequired && actionRequiredStatuses.length === 0;

  useEffect(() => {
    if (!teamFilterReady || actionRequiredEmpty) {
      return;
    }
    dispatch(searchOrders({ filters: effectiveFilters, pagination: paginationState }));
    dispatch(searchStandbyOrders({ filters: effectiveFilters, pagination: paginationState }));
  }, [actionRequiredEmpty, dispatch, effectiveFilters, paginationState, teamFilterReady]);

  const persistListParams = (nextFilters: OrderSearchFilters, nextPagination: PaginationParam) => {
    const newParams = new URLSearchParams();
    if (nextFilters.status) {
      newParams.set("status", nextFilters.status);
    }
    if (nextFilters.teamOperationsId) {
      newParams.set("teamOperationsId", nextFilters.teamOperationsId);
    }
    newParams.set("page", nextPagination.page.toString());
    newParams.set("pageSize", nextPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const handleSearch = async (newFilters: OrderSearchFilters, newPagination: PaginationParam) => {
    persistListParams(newFilters, newPagination);
    setFilters(newFilters);
    setPaginationState(newPagination);
  };

  const handlePaginationChange = (newPagination: PaginationParam) => {
    persistListParams(filters, newPagination);
    setPaginationState(newPagination);
  };

  const enterReportSelectMode = useCallback(() => {
    setSelectedMembersById({});
    setReportSelectMode(true);
  }, []);

  const exitReportSelectMode = useCallback(() => {
    setReportSelectMode(false);
    setSelectedMembersById({});
  }, []);

  useEffect(() => {
    if (!reportSelectMode || submittingReport) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.getAttribute("aria-expanded") === "true") {
        return;
      }
      event.preventDefault();
      exitReportSelectMode();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [exitReportSelectMode, reportSelectMode, submittingReport]);

  const toggleTripSelection = (order: Order) => {
    if (!canSelectOrderForReport(order, selectedTeamOperationsId)) {
      return;
    }
    setSelectedMembersById((current) => {
      if (current[order._id]) {
        const { [order._id]: _, ...rest } = current;
        return rest;
      }
      return {
        ...current,
        [order._id]: { kind: "trip", teamOperationsId: order.teamOperationsId },
      };
    });
  };

  const toggleStandbySelection = (order: StandbyOrder) => {
    if (!canSelectStandbyForReport(order, selectedTeamOperationsId)) {
      return;
    }
    setSelectedMembersById((current) => {
      if (current[order._id]) {
        const { [order._id]: _, ...rest } = current;
        return rest;
      }
      return {
        ...current,
        [order._id]: { kind: "standby", teamOperationsId: order.teamOperationsId },
      };
    });
  };

  const handleCreateReport = async () => {
    if (selectedIds.length === 0) {
      toastEmitter.emit(t("reports.validation.membersRequired" as TranslationKeys), "error");
      return;
    }

    const teamIds = new Set(selectedIds.map((id) => selectedMembersById[id].teamOperationsId));
    if (teamIds.size !== 1) {
      toastEmitter.emit(t("reports.validation.mixedTeams" as TranslationKeys), "error");
      return;
    }

    const teamOperationsId = selectedMembersById[selectedIds[0]].teamOperationsId;
    const orderIds = selectedIds.filter((id) => selectedMembersById[id].kind === "trip");
    const standbyOrderIds = selectedIds.filter((id) => selectedMembersById[id].kind === "standby");
    setSubmittingReport(true);
    try {
      const report = await createUangJalanReportApi({
        teamOperationsId,
        ...(orderIds.length > 0 ? { orderIds } : {}),
        ...(standbyOrderIds.length > 0 ? { standbyOrderIds } : {}),
      });
      toastEmitter.emit(t("reports.createdSuccessfully" as TranslationKeys), "success");
      exitReportSelectMode();
      navigate(`/uang-jalan-reports/${report.readableId}`);
    } catch (err: any) {
      toastEmitter.emit(getErrorMessage(err, t, "createUangJalanReport"), "error");
    } finally {
      setSubmittingReport(false);
    }
  };

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error" sx={{ mt: 3 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  const listCount = combinedTotal || orders.length + standbyOrders.length;
  const listSubtitle =
    listCount > 0
      ? t("orders.listSubtitle" as TranslationKeys, { count: listCount })
      : t("orders.listSubtitleEmpty" as TranslationKeys);
  const pageTitle = isActionRequired
    ? t("sidebar.actionRequired" as TranslationKeys)
    : t("Orders" as TranslationKeys);

  const headerActions = reportSelectMode ? (
    <>
      <Button
        variant="outlined"
        onClick={exitReportSelectMode}
        data-testid={TestId.ORDER.CREATE_REPORT_CANCEL}
        sx={headerButtonSx}
      >
        {t("orders.createReportCancel" as TranslationKeys)}
      </Button>
      <Button
        variant="contained"
        disabled={selectedIds.length === 0 || submittingReport}
        onClick={handleCreateReport}
        data-testid={TestId.ORDER.CREATE_REPORT_SUBMIT}
        sx={headerButtonSx}
      >
        {t("orders.createReportSubmit" as TranslationKeys)}
        {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
      </Button>
    </>
  ) : (
    <>
      {canCreateReport && (
        <Button
          variant="outlined"
          onClick={enterReportSelectMode}
          data-testid={TestId.ORDER.CREATE_REPORT_BUTTON}
          sx={headerButtonSx}
        >
          {t("orders.createReport" as TranslationKeys)}
        </Button>
      )}
      {canAddOrder && (
        <DetailHeaderSplitActions
          primary={{
            id: "add-order",
            label: t("Add Order" as TranslationKeys),
            icon: <AddIcon />,
            onClick: () => navigate("/orders/new"),
            testId: TestId.ORDER.ADD_BUTTON,
          }}
          menuActions={[
            {
              id: "add-standby",
              label: t("standby.add" as TranslationKeys),
              onClick: () => navigate("/orders/standby/new"),
              testId: TestId.STANDBY.ADD_BUTTON,
            },
          ]}
          menuTestId={TestId.ORDER.ADD_MENU}
        />
      )}
    </>
  );

  return (
    <Box sx={{ pb: 4 }}>
      <DetailPageHeader
        title={pageTitle}
        subtitle={listSubtitle}
        metaLine={
          reportSelectMode ? t("orders.createReportModeHint" as TranslationKeys) : undefined
        }
        actions={headerActions}
      />

      {selectedTeam && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2,
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            maxWidth: { sm: 360 },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha("#3B82F6", 0.12),
              color: "#3B82F6",
              flexShrink: 0,
            }}
          >
            <AccountBalanceWalletOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t("Outstanding Saldo" as TranslationKeys)} ({selectedTeam.name})
            </Typography>
            <Typography
              variant="h6"
              component="p"
              data-testid={TestId.ORDER.OUTSTANDING}
              sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}
            >
              {formatRupiah(selectedTeam.outstandingBalance)}
            </Typography>
          </Box>
        </Paper>
      )}

      {!teamFilterReady || (loading && orders.length === 0 && standbyOrders.length === 0) ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <OrderTable
          orders={actionRequiredEmpty ? [] : orders}
          standbyOrders={actionRequiredEmpty ? [] : standbyOrders}
          teams={operationsTeams}
          filters={filters}
          loading={loading}
          pagination={pagination}
          onFiltersChange={setFilters}
          onSearch={handleSearch}
          onPaginationChange={handlePaginationChange}
          reportSelectMode={reportSelectMode}
          selectedOrderIds={selectedIds}
          selectedTeamOperationsId={selectedTeamOperationsId}
          onToggleOrderSelection={toggleTripSelection}
          onToggleStandbySelection={toggleStandbySelection}
          canAddOrder={canAddOrder}
          onAddOrder={() => navigate("/orders/new")}
        />
      )}
    </Box>
  );
};

export default OrdersPage;
